/**
 * Cookies de sesión (únicos tokens en el navegador).
 * Nombres con prefijo dpms_ para no chocar con otros proyectos en localhost
 * (p. ej. ldap-auth-service también usa «auth_token»).
 */
import Cookies from 'js-cookie';

const COOKIE_OPTS = {
  path: '/',
  sameSite: 'Lax',
  secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
};
const TOKEN_MAX_AGE_DAYS = 7;

/** Nombres actuales (esta app). */
export const COOKIE_KEYS = {
  ADMIN_TOKEN: 'dpms_admin_token',
  AUTH_TOKEN: 'dpms_auth_token',
  CART_SESSION: 'cart_session_id',
};

/** Nombres viejos: migrar o descartar (colisión con otros sistemas). */
const LEGACY_AUTH = 'auth_token';
const LEGACY_ADMIN = 'admin_token';

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string' || token.split('.').length < 2) return null;
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '='));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** JWT emitido por el microservicio DentalPro (no LDAP ni otros). */
export function isAppAccessToken(token, expectedUserType) {
  const payload = decodeJwtPayload(token);
  if (!payload || payload.type !== 'access') return false;
  if (payload.iss === 'ldap-auth-service') return false;
  if (expectedUserType && payload.user_type !== expectedUserType) return false;
  if (!expectedUserType && payload.user_type !== 'customer' && payload.user_type !== 'admin') {
    return false;
  }
  if (payload.exp && Number(payload.exp) * 1000 < Date.now()) return false;
  return true;
}

export function removeToken(key) {
  const variants = [
    COOKIE_OPTS,
    { path: '/' },
    { path: '/', secure: true },
    { path: '/', secure: false },
    { path: '/', sameSite: 'Lax' },
    { path: '/', sameSite: 'Strict' },
    { path: '/', domain: 'localhost' },
    { path: '/', domain: 'localhost', secure: false },
  ];
  for (const opts of variants) {
    try {
      Cookies.remove(key, opts);
    } catch {
      /* noop */
    }
  }
}

export function setToken(key, value, maxAgeDays = TOKEN_MAX_AGE_DAYS) {
  if (!value) return;
  Cookies.set(key, value, { ...COOKIE_OPTS, expires: maxAgeDays });
}

/**
 * Lee el token de la app. Migra cookies legacy válidas y descarta JWT ajenos
 * (p. ej. ldap-auth-service) que bloqueaban el login en localhost.
 */
export function getToken(key) {
  const isAuth = key === COOKIE_KEYS.AUTH_TOKEN;
  const isAdmin = key === COOKIE_KEYS.ADMIN_TOKEN;
  const expectedType = isAuth ? 'customer' : isAdmin ? 'admin' : null;

  let value = Cookies.get(key) || null;

  if (!value && isAuth) {
    const legacy = Cookies.get(LEGACY_AUTH) || null;
    if (legacy && isAppAccessToken(legacy, 'customer')) {
      setToken(COOKIE_KEYS.AUTH_TOKEN, legacy);
      value = legacy;
    }
    removeToken(LEGACY_AUTH);
  }

  if (!value && isAdmin) {
    const legacy = Cookies.get(LEGACY_ADMIN) || null;
    if (legacy && isAppAccessToken(legacy, 'admin')) {
      setToken(COOKIE_KEYS.ADMIN_TOKEN, legacy);
      value = legacy;
    }
    removeToken(LEGACY_ADMIN);
  }

  if (value && expectedType && !isAppAccessToken(value, expectedType)) {
    removeToken(key);
    if (isAuth) removeToken(LEGACY_AUTH);
    if (isAdmin) removeToken(LEGACY_ADMIN);
    return null;
  }

  return value;
}

/** Limpia tokens propios y legacy (best-effort; HttpOnly ajenos no se pueden borrar desde JS). */
export function clearAllAuth() {
  removeToken(COOKIE_KEYS.ADMIN_TOKEN);
  removeToken(COOKIE_KEYS.AUTH_TOKEN);
  removeToken(LEGACY_ADMIN);
  removeToken(LEGACY_AUTH);
}

export function setCartSessionId(sessionId) {
  if (!sessionId || typeof sessionId !== 'string') return;
  Cookies.set(COOKIE_KEYS.CART_SESSION, sessionId, { ...COOKIE_OPTS, expires: 30 });
}

export function getCartSessionId() {
  return Cookies.get(COOKIE_KEYS.CART_SESSION) || null;
}
